import { useTranslation } from 'react-i18next';

interface PaginationProps {
  totalCount: number;
  currentPage: number;
  perPage: number;
  onPageChange: (newPage: number) => void;
  disabled?: boolean;
}

export const Pagination = ({
  totalCount,
  currentPage,
  perPage,
  onPageChange,
  disabled = false,
}: PaginationProps) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalCount / perPage);

  // 1ページ以下の場合は表示しない
  if (totalPages <= 1) {
    return null;
  }

  // 表示するページ番号を計算
  const getPageNumbers = (): (number | 'dots')[] => {
    const pages: (number | 'dots')[] = [];
    const maxVisiblePages = 7; // 最大表示ページ数

    if (totalPages <= maxVisiblePages) {
      // 全ページ表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 現在ページを中心に表示
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);

      // 最初のページ
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('dots');
        }
      }

      // 中央のページ
      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      // 最後のページ
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('dots');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handlePageClick = (page: number) => {
    if (!disabled && page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <div className="e-pagination">
      {/* 最初のページへ */}
      <button
        className={`e-pagination__control e-pagination__control--first ${isFirstPage ? 'is-disabled' : ''}`}
        type="button"
        onClick={() => handlePageClick(1)}
        disabled={disabled || isFirstPage}
        aria-label={t('staffOnly.pagination.first')}
      >
        «
      </button>

      {/* 前のページへ */}
      <button
        className={`e-pagination__control e-pagination__control--prev ${isFirstPage ? 'is-disabled' : ''}`}
        type="button"
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={disabled || isFirstPage}
        aria-label={t('staffOnly.pagination.prev')}
      >
        ‹
      </button>

      {/* ページ番号 */}
      {pageNumbers.map((page, index) =>
        page === 'dots' ? (
          <span
            key={`dots-${index}`}
            className="e-pagination__dots"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            key={page}
            className={`e-pagination__item ${page === currentPage ? 'is-current' : ''}`}
            type="button"
            onClick={() => handlePageClick(page)}
            disabled={disabled}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        ),
      )}

      {/* 次のページへ */}
      <button
        className={`e-pagination__control e-pagination__control--next ${isLastPage ? 'is-disabled' : ''}`}
        type="button"
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={disabled || isLastPage}
        aria-label={t('staffOnly.pagination.next')}
      >
        ›
      </button>

      {/* 最後のページへ */}
      <button
        className={`e-pagination__control e-pagination__control--last ${isLastPage ? 'is-disabled' : ''}`}
        type="button"
        onClick={() => handlePageClick(totalPages)}
        disabled={disabled || isLastPage}
        aria-label={t('staffOnly.pagination.last')}
      >
        »
      </button>
    </div>
  );
};
